import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { useSessionExtender } from "./hooks/useSessionExtender";
import ProtectedRoute from "./auth/ProtectedRoute";
import Login from "./auth/Login";
import Register from "./auth/Register";
import ResetPassword from "./auth/ResetPassword";
import Unauthorized from "./auth/Unauthorized";
import Home from "./pages/Home";
import InventoryOverview from "./pages/InventoryOverview";
import ProductList from "./pages/ProductList";
import ProductDetail from "./pages/ProductDetail";
import AddProductPage from "./pages/AddProductPage";
import BrandList from "./pages/BrandList";
import LocationList from "./pages/LocationList";
import StockCountList from "./pages/StockCountList";
import StockCountForm from "./pages/StockCountForm";
import StockCountScan from "./pages/StockCountScan";
import PurchaseList from "./pages/PurchaseList";
import InvoiceForm from "./pages/InvoiceForm";
import SaleList from "./pages/SaleList";
import SupplierList from "./pages/SupplierList";
import UserDetail from "./pages/UserDetail";
import SecurityOverview from "./pages/SecurityOverview";
import UserList from "./pages/UserList";
import RoleList from "./pages/RoleList";
import RoleDetail from "./pages/RoleDetail";
import Navbar from "./components/Navbar";
import Loader from "./components/Loader";
import Footer from "./components/Footer";
import ProtectedRouteWithPermission from "./auth/ProtectedRouteWithPermission";
import ResultsInkomstenfile from "./pages/results/ResultsInkomstenfile";
import ResultsConsultations from "./pages/results/ResultsConsultations";
import ResultsMemberConfig from "./pages/results/ResultsMemberConfig";
import ResultsManualEntries from "./pages/results/ResultsManualEntries";
import ContractLines from "./pages/timesheets/ContractLines";
import TimesheetsPage from "./pages/timesheets/TimesheetsPage";
import Approvals from "./pages/timesheets/Approvals";
import BoekjeSaskia from "./pages/income/BoekjeSaskia";

function AuthenticatedApp() {
  const { user, loading, logout, isUnauthorized, permissions } = useAuth();

  // Automatically extend session when user is active
  // This prevents session expiration during form filling (e.g., stock counts)
  useSessionExtender({ enabled: !!user });

  console.log("[AuthenticatedApp] Render - loading:", loading, "user:", user ? user.email : "null", "permissions:", permissions?.length || 0);

  if (loading) {
    console.log("[AuthenticatedApp] Loading is true, showing Loader");
    return <Loader />;
  }

  console.log("[AuthenticatedApp] Loading is false, rendering routes");
  return (
    <>
      {user && <Navbar user={user} onLogout={logout} />}
      <Routes>
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route
          path="/"
          element={
            <ProtectedRoute user={user} loading={loading} isUnauthorized={isUnauthorized}>
              <Home user={user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory-overview"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              anyPermissions={[
                "products.view", "brands.view", "locations.view", "suppliers.view",
                "stock_counts.view", "purchases.view", "sales.view", "statistics.view"
              ]}
            >
              <InventoryOverview user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/products"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="products.view"
            >
              <ProductList user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/products/:id"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="products.view"
            >
              <ProductDetail user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/add-product"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="products.create"
            >
              <AddProductPage user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/brands"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="brands.view"
            >
              <BrandList user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/locations"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="locations.view"
            >
              <LocationList user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/stock-counts"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="stock_counts.view"
            >
              <StockCountList user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/stock-counts/new"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="stock_counts.create"
            >
              <StockCountForm user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/stock-counts/scan"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="stock_counts.create"
            >
              <StockCountScan user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/stock-counts/:id"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="stock_counts.view"
            >
              <StockCountForm user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/purchases"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="purchases.view"
            >
              <PurchaseList user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/purchases/new"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="purchases.create"
            >
              <InvoiceForm user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/purchases/:id"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="purchases.view"
            >
              <InvoiceForm user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/sales"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="sales.view"
            >
              <SaleList user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/suppliers"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="suppliers.view"
            >
              <SupplierList user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute user={user} loading={loading} isUnauthorized={isUnauthorized}>
              <UserDetail user={user} self />
            </ProtectedRoute>
          }
        />
        <Route
          path="/security-overview"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              anyPermissions={["users.view", "roles.view"]}
            >
              <SecurityOverview user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="users.view"
            >
              <UserList user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route path="/profiles" element={<Navigate to="/roles" replace />} />
        <Route
          path="/users/:userId"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="users.view"
            >
              <UserDetail user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/roles/:roleId"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="roles.view"
            >
              <RoleDetail />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/roles"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="roles.view"
            >
              <RoleList user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/results/inkomstenfile"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="results.view"
            >
              <ResultsInkomstenfile user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/results/consultations"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="results.view"
            >
              <ResultsConsultations user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/results/manual"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="results.import"
            >
              <ResultsManualEntries user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/results/config"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="results.manage"
            >
              <ResultsMemberConfig user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route path="/results/prestaties" element={<Navigate to="/timesheets" replace />} />
        <Route
          path="/timesheets"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              anyPermissions={["timesheets.view_own", "timesheets.view_all"]}
            >
              <TimesheetsPage />
            </ProtectedRouteWithPermission>
          }
        />
        <Route path="/timesheets/year" element={<Navigate to="/timesheets?tab=year" replace />} />
        <Route
          path="/timesheets/approvals"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              anyPermissions={["timesheets.approve", "timesheets.release"]}
            >
              <Approvals />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/timesheets/admin"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="contracts.manage"
            >
              <ContractLines user={user} />
            </ProtectedRouteWithPermission>
          }
        />
        <Route
          path="/inkomsten/boekje-saskia"
          element={
            <ProtectedRouteWithPermission
              user={user}
              loading={loading}
              isUnauthorized={isUnauthorized}
              permissions={permissions}
              permission="boekje_saskia.view"
            >
              <BoekjeSaskia user={user} />
            </ProtectedRouteWithPermission>
          }
        />
      </Routes>
      {user && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public routes - no authentication required */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* All other routes go through authenticated app */}
          <Route path="/*" element={<AuthenticatedApp />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}
